import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { X } from "@phosphor-icons/react";

export const TOUR_EVENT = "cr-demo-tour";
const SEEN = "cr-demo-tour-seen";

const STEPS = [
  { intro: true, title: "Cash Flow Calibration Made Easy", body: "A two-minute look at how CalibRunway turns Stripe, your bank and QuickBooks into one live runway, so every growth move is a calculated one." },
  { sel: '[data-testid="zero-cash-date"]', title: "Zero Cash Date", body: "The exact day cash would run out at today's burn, so founders know their real deadline." },
  { sel: '[data-testid="target-zcd-btn"], [data-testid="target-gap"]', title: "Target date and gap", body: "Pick the date you need to reach (say, your next raise) and see how many months you have to close." },
  { sel: '[data-testid="runway-chart"]', title: "Runway chart", body: "Actual cash, the baseline forecast and saved scenarios on one chart. The separate dotted line is the weighted sales pipeline, never mixed into the baseline." },
  { sel: '[data-testid="tri-sync-btn"]', title: "Multi-Sync", body: "One click pulls Stripe revenue, bank balances and QuickBooks bills into the model, with duplicate protection." },
  { sel: '[data-testid="revenue-trend-card"]', title: "Revenue trend", body: "Six months of subscription MRR and cash collected, straight from Stripe." },
  { sel: '[data-testid="ai-calibrator"]', title: "AI Calibrator", body: "Ask a what-if in plain English, like \"What if we hire two engineers?\", and get the runway impact as a scenario." },
  { sel: '[data-testid="scenario-panel"]', title: "Saved scenarios", body: "Compare plans side by side. Teammates comment, and the founder marks each one Approved or Rejected." },
  { sel: '[data-testid="nav-alerts"], [data-testid="m-nav-alerts"]', title: "Board reports", body: "Monthly investor PDFs and weekly digests, generated from the same live numbers." },
];

const visible = (s) => (s.intro ? document.body : [...document.querySelectorAll(s.sel)].find((el) => el.getClientRects().length > 0));

const Intro = ({ step, total, onNext, onClose }) => (
  <div data-testid="demo-tour-intro" role="dialog" aria-label={step.title}
    className="fixed left-1/2 top-1/2 z-[91] w-[min(420px,calc(100vw-24px))] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-[#B026FF]/50 bg-[#0A0A0A] p-7 text-center shadow-[0_20px_60px_rgba(0,0,0,0.7)] animate-in fade-in-0 zoom-in-95">
    <img src="/calibrunway-mark.png" alt="CalibRunway" data-testid="demo-tour-logo" className="mx-auto h-14 w-14" />
    <p className="mt-3 font-mono text-[10px] text-[#D9A6FF]">CALIBRUNWAY · {total - 1} QUICK TIPS</p>
    <h2 data-testid="demo-tour-pitch" className="font-display text-2xl text-white mt-2">{step.title}</h2>
    <p className="mt-2 text-sm text-[#A3A3A3] leading-relaxed">{step.body}</p>
    <div className="mt-6 flex justify-center gap-2">
      <button data-testid="demo-tour-skip" onClick={onClose} className="rounded-full border border-[#2A2A2A] px-4 py-2 text-xs hover:border-white transition-colors">Skip</button>
      <button data-testid="demo-tour-next" onClick={onNext} className="rounded-full bg-[#B026FF] px-5 py-2 text-xs font-semibold text-white hover:bg-[#C45CFF] transition-colors">Start the tour</button>
    </div>
  </div>
);

const useRect = (el) => {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    if (!el) return undefined;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const update = () => setRect(el.getBoundingClientRect());
    update();
    const t = setTimeout(update, 450);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { clearTimeout(t); window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [el]);
  return rect;
};

const Card = ({ rect, step, i, total, onNext, onBack, onClose }) => {
  const w = Math.min(340, window.innerWidth - 24);
  const tall = rect.height > window.innerHeight * 0.5;
  const beside = !tall ? null : (rect.right + w + 28 < window.innerWidth ? rect.right + 14 : rect.left - w - 14 > 12 ? rect.left - w - 14 : null);
  const below = rect.bottom + 200 < window.innerHeight;
  const top = beside != null ? Math.max(12, Math.min(rect.top + 12, window.innerHeight - 220)) : below ? rect.bottom + 14 : Math.max(12, rect.top - 14 - 190);
  const left = beside != null ? beside : Math.min(Math.max(12, rect.left), window.innerWidth - w - 12);
  return (
    <div data-testid="demo-tour-card" role="dialog" aria-label={step.title} style={{ top, left, width: w }}
      className="fixed z-[91] rounded-2xl border border-[#B026FF]/50 bg-[#0A0A0A] p-5 shadow-[0_20px_60px_rgba(0,0,0,0.7)] animate-in fade-in-0 zoom-in-95">
      <div className="flex items-start justify-between gap-3">
        <p className="font-mono text-[10px] text-[#D9A6FF]">TIP {i} OF {total - 1}</p>
        <button data-testid="demo-tour-close" onClick={onClose} aria-label="Close tour" className="text-[#6B6B6B] hover:text-white transition-colors"><X size={14} /></button>
      </div>
      <p data-testid="demo-tour-title" className="font-display text-lg text-white mt-1">{step.title}</p>
      <p className="mt-1.5 text-sm text-[#A3A3A3] leading-relaxed">{step.body}</p>
      <div className="mt-4 flex items-center gap-2">
        <button data-testid="demo-tour-skip" onClick={onClose} className="text-xs text-[#6B6B6B] hover:text-white transition-colors">Skip tour</button>
        <div className="ml-auto flex gap-2">
          {i > 1 && <button data-testid="demo-tour-back" onClick={onBack} className="rounded-full border border-[#2A2A2A] px-3 py-1.5 text-xs hover:border-white transition-colors">Back</button>}
          <button data-testid="demo-tour-next" onClick={onNext} className="rounded-full bg-[#B026FF] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#C45CFF] transition-colors">{i + 1 === total ? "Finish" : "Next"}</button>
        </div>
      </div>
    </div>
  );
};

export const DemoTour = () => {
  const [steps, setSteps] = useState(null);
  const [i, setI] = useState(0);
  const start = useCallback(() => { setSteps(STEPS.filter(visible)); setI(0); }, []);
  const close = () => { setSteps(null); sessionStorage.setItem(SEEN, "1"); };
  useEffect(() => {
    window.addEventListener(TOUR_EVENT, start);
    const t = sessionStorage.getItem(SEEN) ? null : setTimeout(start, 1200);
    return () => { window.removeEventListener(TOUR_EVENT, start); clearTimeout(t); };
  }, [start]);
  useEffect(() => {
    if (!steps) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [steps]);
  const step = steps?.[i];
  const el = step && !step.intro ? visible(step) : null;
  const rect = useRect(el);
  if (step?.intro) return (
    <>
      <div data-testid="demo-tour-overlay" onClick={close} className="fixed inset-0 z-[89] bg-black/60 backdrop-blur-sm" />
      <Intro step={step} total={steps.length} onClose={close} onNext={() => setI(1)} />
    </>
  );
  if (!step || !rect) return null;
  const pad = 6;
  return (
    <>
      <div data-testid="demo-tour-overlay" onClick={close} className="fixed inset-0 z-[89] bg-black/40" />
      <div data-testid="demo-tour-highlight" className="pointer-events-none fixed z-[90] rounded-xl ring-2 ring-[#B026FF] shadow-[0_0_0_4px_rgba(176,38,255,0.25)] transition-all duration-300"
        style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }} />
      <Card rect={rect} step={step} i={i} total={steps.length} onClose={close}
        onBack={() => setI(i - 1)} onNext={() => (i + 1 === steps.length ? close() : setI(i + 1))} />
    </>
  );
};
